import { Badge, Card, CardBody, CardHeader, Col, Row, Table } from "reactstrap";
import { mediaUrl } from "../../helpers/mediaHelper";
import { userStatusLabel } from "../../helpers/userStatusHelper";
import PropTypes from "prop-types";

const LABEL_MAP = {
  weekdays: "Weekdays",
  weekend: "Weekends",
  nights: "Nights",
  bankHoliday: "Bank holidays",
  urgentCover: "Urgent cover",
  car: "Car",
  public: "Public transport",
  walk: "Walk",
};

function hasText(value) {
  return value != null && String(value).trim() !== "";
}

function labelize(value) {
  if (!hasText(value)) return "";
  const key = String(value).trim();
  if (LABEL_MAP[key]) return LABEL_MAP[key];
  return key
    .replace(/_/g, " ")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatList(values) {
  if (!Array.isArray(values) || values.length === 0) return null;
  const labels = values.map(labelize).filter(Boolean);
  return labels.length ? labels.join(", ") : null;
}

function formatDisplayDate(value) {
  if (!hasText(value)) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString();
}

function normalizeRtwVerificationStatus(value) {
  if (value === true || String(value).toLowerCase() === "verified") return true;
  if (value === false || String(value).toLowerCase() === "unverified")
    return false;
  return null;
}

function ProfileSection({ title, children, className = "mb-3" }) {
  return (
    <Card className={`border-0 shadow-sm ${className}`}>
      <CardHeader className="bg-light py-2">
        <h6 className="mb-0">{title}</h6>
      </CardHeader>
      <CardBody>{children}</CardBody>
    </Card>
  );
}

ProfileSection.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

/**
 * Client-safe worker snapshot for booking and review surfaces.
 * Shows presentation fields and admin-approved compliance only.
 */
export function WorkerMiniProfile({ user }) {
  if (!user || typeof user !== "object") {
    return <div className="text-muted py-4 text-center">No profile data.</div>;
  }

  const worker = user.worker || {};
  const pi = worker.personalInformation || {};
  const prefs = worker.workPreferences || {};
  const comp = worker.complianceSkills || {};
  const rtw = worker.rightToWork || {};
  const rtwVerificationStatus = normalizeRtwVerificationStatus(
    rtw.status ?? rtw.rtwStatus,
  );
  const may = worker.moreAboutYou || {};

  const selfieSrc =
    mediaUrl(pi.uploadSelfie) || mediaUrl(user.photo) || "";
  const displayName =
    user.fullName ||
    [pi.firstName, pi.surName].filter(Boolean).join(" ").trim() ||
    pi.firstName ||
    "Worker";

  const summaryText =
    String(may.aboutYouAndWorkHistory || may.aboutYouWorkHistory || "").trim() ||
    String(pi.summary || user.summary || "").trim();

  const experienceLevel = hasText(pi.experienceLevel)
    ? String(pi.experienceLevel)
    : hasText(user.experienceLevel)
    ? String(user.experienceLevel)
    : "";

  const videoSrc = mediaUrl(rtw.onscreeningVideo);

  const eligibleSectorRoles = [];
  if (Array.isArray(prefs.sectors)) {
    for (const entry of prefs.sectors) {
      const sectorTitle =
        entry?.sector?.title || entry?.sector?.slug || "Sector";
      const roles = Array.isArray(entry?.roles) ? entry.roles : [];
      for (const r of roles) {
        if (r?.isEligible) {
          eligibleSectorRoles.push({
            sector: sectorTitle,
            role: r?.role?.title || r?.role?.slug || "Role",
          });
        }
      }
    }
  }

  const approvedDbs = (Array.isArray(comp.dbs) ? comp.dbs : []).filter(
    (item) => item?.status === true
  );
  const approvedOtherCerts = (
    Array.isArray(comp.otherCerts) ? comp.otherCerts : []
  ).filter((item) => item?.status === true);

  const showDrivingLicence = rtw.drivingLicenseStatus === true;
  const showKyc = rtw.kycStatus === true;
  const showTrustId = rtw.trustIdStatus === true;

  const availabilityText = formatList(prefs.availability);
  const transportText = formatList(prefs.transport);
  const languages = Array.isArray(prefs.languages)
    ? prefs.languages.filter(Boolean)
    : [];
  const whatMattersMost = Array.isArray(prefs.whatMattersMost)
    ? prefs.whatMattersMost.filter(Boolean)
    : [];

  const ratingStr = pi.ratingsAverage != null ? String(pi.ratingsAverage) : null;
  const reliability = pi.reliabilityScore;
  const strikeCount = worker.strikeCount;
  const hasStrikes = strikeCount != null && Number(strikeCount) > 0;

  const hasClearances =
    rtw.rightToWorkInUk != null ||
    hasText(rtw.rightToWorkStatus) ||
    approvedDbs.length > 0 ||
    approvedOtherCerts.length > 0 ||
    showDrivingLicence ||
    showKyc ||
    showTrustId;

  return (
    <div className="worker-mini-profile">
      <Card className="border-0 shadow-sm mb-3">
        <CardBody>
          <Row className="align-items-center gy-3">
            <Col xs="auto">
              <div className="worker-mini-profile__avatar rounded-circle border bg-light overflow-hidden d-flex align-items-center justify-content-center">
                {selfieSrc ? (
                  <img
                    src={selfieSrc}
                    alt=""
                    className="w-100 h-100 worker-mini-profile__avatar-img"
                  />
                ) : (
                  <i
                    className="mdi mdi-account display-4 text-muted"
                    aria-hidden
                  />
                )}
              </div>
            </Col>
            <Col>
              <h4 className="mb-1">{displayName}</h4>
              <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
                {user.tier && (
                  <Badge color="info" pill className="fw-semibold">
                    Tier: {labelize(user.tier)}
                  </Badge>
                )}
                {user.status && (
                  <Badge color="secondary" pill>
                    {userStatusLabel(user.status)}
                  </Badge>
                )}
                {rtwVerificationStatus === true ? (
                  <Badge color="success" pill>
                    Verified
                  </Badge>
                ) : (
                  <Badge color="warning" pill className="text-dark">
                    Unverified
                  </Badge>
                )}
                {Array.isArray(worker.badges) && worker.badges.length > 0 &&
                  worker.badges.map((badge, index) => (
                    <Badge key={index} color="primary" pill>
                      {typeof badge === "string"
                        ? badge
                        : badge?.title || badge?.name || "Badge"}
                    </Badge>
                  ))}
              </div>
              <Row className="gy-2 small">
                {hasText(pi.nationality) && (
                  <Col xs={12} sm={6} md={4}>
                    <span className="text-muted">Nationality: </span>
                    {pi.nationality}
                  </Col>
                )}
                {hasText(experienceLevel) && (
                  <Col xs={12} sm={6} md={4}>
                    <span className="text-muted">Experience: </span>
                    {experienceLevel}
                  </Col>
                )}
                {hasText(pi.city) && (
                  <Col xs={12} sm={6} md={4}>
                    <span className="text-muted">Location: </span>
                    {[pi.city, pi.county].filter(Boolean).join(", ")}
                  </Col>
                )}
              </Row>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {hasText(summaryText) && (
        <ProfileSection title="Summary">
          <p className="mb-0 text-muted worker-mini-profile__summary">
            {summaryText}
          </p>
        </ProfileSection>
      )}

      {videoSrc && (
        <ProfileSection title="Introduction video">
          <video
            src={videoSrc}
            controls
            className="w-100 rounded border bg-dark worker-mini-profile__video"
            preload="metadata"
          />
        </ProfileSection>
      )}

      {languages.length > 0 && (
        <ProfileSection title="Spoken languages">
          <div className="d-flex flex-wrap gap-2">
            {languages.map((lang) => (
              <Badge key={lang} color="primary" outline className="fw-normal">
                {lang}
              </Badge>
            ))}
          </div>
        </ProfileSection>
      )}

      {(availabilityText ||
        transportText ||
        prefs.preferredWorkRadius != null ||
        whatMattersMost.length > 0) && (
        <ProfileSection title="Work preferences">
          <Row className="gy-2 small">
            {availabilityText && (
              <Col md={6}>
                <span className="text-muted d-block">Availability</span>
                {availabilityText}
              </Col>
            )}
            {transportText && (
              <Col md={6}>
                <span className="text-muted d-block">Transport</span>
                {transportText}
              </Col>
            )}
            {prefs.preferredWorkRadius != null &&
              prefs.preferredWorkRadius !== "" && (
                <Col md={6}>
                  <span className="text-muted d-block">Preferred radius</span>
                  {prefs.preferredWorkRadius} miles
                </Col>
              )}
            {whatMattersMost.length > 0 && (
              <Col md={12}>
                <span className="text-muted d-block mb-1">Priorities</span>
                <div className="d-flex flex-wrap gap-2">
                  {whatMattersMost.map((item) => (
                    <Badge key={item} color="light" className="text-dark border">
                      {item}
                    </Badge>
                  ))}
                </div>
              </Col>
            )}
          </Row>
        </ProfileSection>
      )}

      <Card className="mb-3 border-0 shadow-sm">
        <CardHeader className="bg-light py-2">
          <h6 className="mb-0">Eligible sectors &amp; roles</h6>
        </CardHeader>
        <CardBody className="p-0">
          {eligibleSectorRoles.length === 0 ? (
            <div className="p-3 text-muted">No eligible roles listed.</div>
          ) : (
            <Table size="sm" className="mb-0 align-middle">
              <thead className="table-light">
                <tr>
                  <th>Sector</th>
                  <th>Role</th>
                </tr>
              </thead>
              <tbody>
                {eligibleSectorRoles.map((row, idx) => (
                  <tr key={`${row.sector}-${row.role}-${idx}`}>
                    <td className="text-capitalize">{row.sector}</td>
                    <td className="text-capitalize">{row.role}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      {hasClearances && (
        <ProfileSection title="Approved clearances &amp; certificates">
          <Row className="gy-3">
            <Col md={6}>
              <small className="text-muted fw-bold d-block mb-2">
                Right to work
              </small>
              <ul className="list-unstyled mb-0 small">
                <li>
                  <span className="text-muted">In UK: </span>
                  {rtw.rightToWorkInUk === true ? (
                    <Badge color="success">Yes</Badge>
                  ) : rtw.rightToWorkInUk === false ? (
                    <Badge color="danger">No</Badge>
                  ) : (
                    "–"
                  )}
                </li>
                {hasText(rtw.rightToWorkStatus) && (
                  <li className="mt-1">
                    <span className="text-muted">Status: </span>
                    {rtw.rightToWorkStatus}
                  </li>
                )}
              </ul>
            </Col>

            <Col md={6}>
              <small className="text-muted fw-bold d-block mb-2">
                DBS (approved)
              </small>
              {approvedDbs.length === 0 ? (
                <span className="text-muted small">None approved</span>
              ) : (
                <ul className="list-unstyled mb-0 small">
                  {approvedDbs.map((item, index) => {
                    const title = item?.dbsCert?.title || "DBS";
                    const ref = item?.serviceNumber || item?.certificateRef;
                    return (
                      <li key={item.dbsCertId || item.dbsSlug || index} className="mb-2">
                        <Badge color="success" className="me-2">
                          {title}
                        </Badge>
                        {hasText(item.dbsType) && (
                          <span className="text-muted me-2">
                            {labelize(item.dbsType)}
                          </span>
                        )}
                        {ref && (
                          <span className="text-muted d-block">Ref: {ref}</span>
                        )}
                        {hasText(item.expiryDate) && (
                          <span className="text-muted d-block">
                            Expires: {formatDisplayDate(item.expiryDate)}
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </Col>
          </Row>

          {approvedOtherCerts.length > 0 && (
            <div className="mt-3 pt-3 border-top">
              <small className="text-muted fw-bold d-block mb-2">
                Other approved certificates
              </small>
              <div className="d-flex flex-wrap gap-2">
                {approvedOtherCerts.map((cert, index) => (
                  <Badge key={cert.CertId || index} color="success" pill>
                    {cert.Cert?.title || cert.certSlug || "Certificate"}
                    {hasText(cert.expiryDate)
                      ? ` · exp. ${formatDisplayDate(cert.expiryDate)}`
                      : ""}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {(showDrivingLicence || showKyc || showTrustId) && (
            <div className="mt-3 pt-3 border-top">
              <small className="text-muted fw-bold d-block mb-2">
                Additional verified checks
              </small>
              <div className="d-flex flex-wrap gap-2">
                {showDrivingLicence && (
                  <Badge color="success" pill>
                    Driving licence
                    {hasText(rtw.drivingLicenseExpiryDate)
                      ? ` · exp. ${formatDisplayDate(rtw.drivingLicenseExpiryDate)}`
                      : ""}
                  </Badge>
                )}
                {showKyc && (
                  <Badge color="success" pill>
                    KYC verified
                    {hasText(rtw.kycExpiry)
                      ? ` · exp. ${formatDisplayDate(rtw.kycExpiry)}`
                      : ""}
                  </Badge>
                )}
                {showTrustId && (
                  <Badge color="success" pill>
                    TrustID verified
                    {hasText(rtw.trustIdExpiry)
                      ? ` · exp. ${formatDisplayDate(rtw.trustIdExpiry)}`
                      : ""}
                  </Badge>
                )}
              </div>
            </div>
          )}
        </ProfileSection>
      )}

      {/* {(hasText(ratingStr) || (reliability != null && reliability !== "") || (strikeCount != null && strikeCount !== "")) && (
        <ProfileSection title="Ratings &amp; conduct" className="mb-0">
          <Row>
            <Col md={4}>
              <small className="text-muted d-block">Star rating (avg.)</small>
              <div className="fs-5">
                {hasText(ratingStr) ? (
                  <>
                    <span className="text-warning me-1" aria-hidden>
                      ★
                    </span>
                    <strong>{ratingStr}</strong>
                    <span className="text-muted small"> / 5</span>
                  </>
                ) : (
                  <span className="text-muted">–</span>
                )}
              </div>
            </Col>
            {reliability != null && reliability !== "" && (
              <Col md={4}>
                <small className="text-muted d-block">Reliability score</small>
                <strong>{reliability}</strong>
              </Col>
            )}
            <Col md={4}>
              <small className="text-muted d-block">Strikes</small>
              {strikeCount != null && strikeCount !== "" ? (
                <div>
                  <Badge color={hasStrikes ? "danger" : "success"} pill>
                    {hasStrikes ? `${strikeCount} strike(s)` : "No strikes"}
                  </Badge>
                  {worker.lastStrikeAt && (
                    <div className="small text-muted mt-1">
                      Last: {new Date(worker.lastStrikeAt).toLocaleString()}
                    </div>
                  )}
                  {worker.suspensionReason && (
                    <div className="small text-danger mt-1">
                      {worker.suspensionReason}
                    </div>
                  )}
                </div>
              ) : (
                <span className="text-muted">–</span>
              )}
            </Col>
          </Row>
        </ProfileSection>
      )} */}
    </div>
  );
}

WorkerMiniProfile.propTypes = {
  user: PropTypes.object.isRequired,
};

export default WorkerMiniProfile;
